import { Injectable } from "@angular/core";
import { SampleGetterState } from "../ngxs/sample/sample-getter.state";
import { first, Observable, tap, switchMap, EMPTY, combineLatest, map } from "rxjs";
import { Select, Store } from "@ngxs/store";
import { LoadSampleInfoIntoState, SetRelevantSample, StoreSampleCount } from "../ngxs/sample/sample.actions";
import { FilterService } from "./filter.service";
import { LocalStorageService } from "./local-storage.service";

@Injectable({
    providedIn: 'root'
})
export class SampleService {
    // Getters and setters
    @Select(SampleGetterState.getSampleCount)
    sampleCount$: Observable<number>;

    @Select(SampleGetterState.getHash)
    sampleHash$: Observable<string>;

    @Select(SampleGetterState.getStoredSampleCounts)
    storedSampleCounts$: Observable<{hash: string, count: number}[]>;

    constructor(
        private store: Store,
        private filterService: FilterService,
        private localStorageService: LocalStorageService
    ) {
    }

    // Functions
    startGettingCountByHashAndType(hash: string, type: string): void {
        function internalSetSampleFunction(type: string, count: number) {
            this.sampleHash$.subscribe((activeButNotSubmittedHash) => {
                if (activeButNotSubmittedHash == hash) {
                    this.setSample(hash, type, count);
                    console.log('Updating count for hash:', hash.slice(0, 8), 'and type:', type, 'to count:', count);
                } else {
                    console.debug('Hash has changed from', hash.slice(0, 8), 'to', activeButNotSubmittedHash.slice(0, 8), '. Not updating count to', count);
                }
            });
        }

        var count: number;
        if (type == 'filters') {
            // First check stored counts
            this.storedSampleCounts$.pipe(
                first(),
                switchMap(storedSampleCounts => {
                    const existingCount = storedSampleCounts.find((sample) => sample.hash === hash);
                    if (existingCount) {
                        console.debug('Count for hash:', hash.slice(0, 8), 'and type:', type, 'is already in the store:', existingCount.count);
                        internalSetSampleFunction(type, existingCount.count);
                        // Return empty observable since we're done
                        return EMPTY;
                    }
                    
                    // If no existing count, check server-side events
                    return this.filterService.serverSideEventHashes$.pipe(
                        first(),
                        tap(hashes => {
                            if (hashes.includes(hash)) {
                                console.debug('Count is already being fetched through SSE events for hash:', hash.slice(0, 8));
                            } else {
                                console.debug('Starting new SSE event for hash:', hash.slice(0, 8), 'and type:', type);
                                this.filterService.getFilterCountsStream(hash);
                            }
                        })
                    );
                })
            ).subscribe();
        } 
        else if (type == 'firmIdList') {
            this.localStorageService.selectedFirmIdsSubject$.pipe(
                first(),
                tap(firmIdList => {
                    count = firmIdList.length;
                    console.debug('Count for hash:', hash.slice(0, 8), 'and type:', type, 'is', count);
                    console.error('This should never be called. Please contact the developer.');
                    internalSetSampleFunction(type, count);
                })
            ).subscribe();
        }      
        
        else {
            console.error(`This should never be called. Please contact the developer. The supplied type was ${type}`);
        }
    }

    // Functions
    loadSampleInfoIntoState() {
        this.store.dispatch(new LoadSampleInfoIntoState());
    }

    setSample(hash: string, type: string, count: number) {
        this.store.dispatch(new SetRelevantSample({ hash, type, count }));
    }

    clearSample() {
        this.store.dispatch(new SetRelevantSample({ hash: null, type: null, count: null, clear: true }));
    }

    storeSampleCount({ hash, type, count }: { hash: string; type: string; count: number }) {
        // Store count in state
        this.store.dispatch(new StoreSampleCount({ hash, count }));

        // Set sample if hashes match
        this.sampleHash$.pipe(
            first(),
            tap(activeButNotSubmittedHash => {
                if (activeButNotSubmittedHash === hash) {
                    this.setSample(hash, type, count);
                } else {
                    const oldHash = hash ? hash.slice(0, 8) : 'null';
                    const newHash = activeButNotSubmittedHash ? activeButNotSubmittedHash.slice(0, 8) : 'null';
                    console.debug('Hash has changed from', oldHash, 'to', newHash, '. Not updating count to', count);
                }
            })
        ).subscribe();
    }
}